import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Settings, Link2, Calendar, Key } from 'lucide-react';
import { autEndpointsApi } from '../services/api';
import { AUTEndpoint, AUTEndpointAuthType } from '../types';

interface EndpointsProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const Endpoints: React.FC<EndpointsProps> = ({ addNotification }) => {
  const [endpoints, setEndpoints] = useState<AUTEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState<AUTEndpoint | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const ENVIRONMENT_OPTIONS = ['Production', 'Staging', 'QA', 'Dev'] as const;
  const AUTH_TYPE_OPTIONS: { value: AUTEndpointAuthType; label: string }[] = [
    { value: 'none', label: 'None' },
    { value: 'bearer', label: 'Bearer token' },
    { value: 'api_key', label: 'API key header' }
  ];

  const [formData, setFormData] = useState({
    name: '',
    environment: '' as '' | (typeof ENVIRONMENT_OPTIONS)[number],
    base_url: '',
    auth_type: 'none' as AUTEndpointAuthType,
    auth_header_name: '',
    auth_token: '',
    description: ''
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    environment: '' as '' | (typeof ENVIRONMENT_OPTIONS)[number],
    base_url: '',
    auth_type: 'none' as AUTEndpointAuthType,
    auth_header_name: '',
    auth_token: '',
    description: ''
  });

  const loadEndpoints = async () => {
    try {
      setLoading(true);
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : undefined;
      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
      if (!orgId || !projectId) {
        setEndpoints([]);
        setLoading(false);
        return;
      }
      const data = await autEndpointsApi.list(orgId, projectId);
      setEndpoints(data);
    } catch (error) {
      addNotification('Failed to load endpoints', 'error');
      console.error('Error loading endpoints:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEndpoints();
  }, []);

  const handleCreateEndpoint = async () => {
    try {
      if (!formData.name.trim()) {
        addNotification('Endpoint name is required', 'error');
        return;
      }
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : undefined;
      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
      if (!orgId || !projectId) {
        addNotification('Please select an organization and project first', 'error');
        return;
      }
      if (formData.auth_type !== 'none' && !formData.auth_token.trim()) {
        addNotification('Auth token is required when endpoint auth is enabled', 'error');
        return;
      }
      await autEndpointsApi.create(orgId, projectId, {
        name: formData.name.trim(),
        environment: formData.environment || undefined,
        base_url: formData.base_url.trim() || undefined,
        auth_type: formData.auth_type,
        auth_header_name: formData.auth_header_name.trim(),
        auth_token: formData.auth_token.trim() || undefined,
        description: formData.description.trim() || undefined
      });
      addNotification('Endpoint created successfully', 'success');
      setShowCreateModal(false);
      setFormData({ name: '', environment: '', base_url: '', auth_type: 'none', auth_header_name: '', auth_token: '', description: '' });
      loadEndpoints();
    } catch (error) {
      addNotification('Failed to create endpoint', 'error');
      console.error('Error creating endpoint:', error);
    }
  };

  const handleDeleteEndpoint = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this endpoint? Endpoints with versions cannot be deleted.')) {
      return;
    }
    try {
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : undefined;
      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
      if (!orgId || !projectId) {
        addNotification('Please select an organization and project first', 'error');
        return;
      }
      await autEndpointsApi.delete(orgId, projectId, id);
      addNotification('Endpoint deleted successfully', 'success');
      loadEndpoints();
    } catch (error) {
      addNotification('Failed to delete endpoint', 'error');
      console.error('Error deleting endpoint:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Endpoints</h1>
            <p className="text-slate-300 mt-2">Manage endpoints for your Application Under Test; versions are associated with each endpoint.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Endpoint
          </button>
        </div>
      </div>

      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700">
          <h3 className="text-lg font-medium text-slate-100">Endpoint List</h3>
        </div>
        {endpoints.length === 0 ? (
          <div className="text-center py-12">
            <Settings className="h-12 w-12 mx-auto mb-3 text-slate-500" />
            <p className="text-slate-400 text-lg">No endpoints created yet</p>
            <p className="text-slate-400 text-sm">Create an endpoint, then add versions under it in Versions.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Endpoint
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead className="bg-slate-700/50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Environment</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">URL</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Auth</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Description</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Created</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-slate-800 divide-y divide-slate-700">
                {endpoints.map((endpoint) => (
                  <tr key={endpoint.id} className="hover:bg-slate-700/30">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-100">{endpoint.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {endpoint.environment ? (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-slate-700 text-slate-300">
                          {endpoint.environment}
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400 max-w-xs truncate" title={endpoint.base_url || ''}>
                      {endpoint.base_url ? (
                        <span className="flex items-center">
                          <Link2 className="h-3 w-3 mr-1 flex-shrink-0" />
                          {endpoint.base_url}
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                      {endpoint.auth_type && endpoint.auth_type !== 'none' ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-indigo-900/60 text-indigo-200">
                          <Key className="h-3 w-3 mr-1" />
                          {endpoint.auth_type === 'bearer' ? 'Bearer' : 'API key'}
                          {endpoint.auth_configured ? '' : ' (missing token)'}
                        </span>
                      ) : (
                        <span className="text-slate-500">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300 max-w-xs truncate" title={endpoint.description || ''}>
                      {endpoint.description || <span className="text-slate-500">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-400">
                      <span className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(endpoint.created_at)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setEditingEndpoint(endpoint);
                            setEditFormData({
                              name: endpoint.name,
                              environment: (endpoint.environment as typeof editFormData.environment) || '',
                              base_url: endpoint.base_url || '',
                              auth_type: endpoint.auth_type || 'none',
                              auth_header_name: endpoint.auth_header_name || '',
                              auth_token: '',
                              description: endpoint.description || ''
                            });
                            setShowEditModal(true);
                          }}
                          className="inline-flex items-center px-3 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteEndpoint(endpoint.id)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-200 bg-red-900/50 hover:bg-red-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Create New Endpoint</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    placeholder="e.g., Main API, Backend Service"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Environment</label>
                  <select
                    value={formData.environment}
                    onChange={(e) => setFormData({ ...formData, environment: e.target.value as typeof formData.environment })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="">Select environment</option>
                    {ENVIRONMENT_OPTIONS.map((env) => (
                      <option key={env} value={env}>{env}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">URL</label>
                  <input
                    type="text"
                    value={formData.base_url}
                    onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Auth Type</label>
                  <select
                    value={formData.auth_type}
                    onChange={(e) => setFormData({ ...formData, auth_type: e.target.value as AUTEndpointAuthType })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    {AUTH_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                {formData.auth_type !== 'none' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-200 mb-1">Auth Header</label>
                      <input
                        type="text"
                        value={formData.auth_header_name}
                        onChange={(e) => setFormData({ ...formData, auth_header_name: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        placeholder={formData.auth_type === 'bearer' ? 'Authorization' : 'X-API-Key'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-200 mb-1">Auth Token *</label>
                      <input
                        type="password"
                        value={formData.auth_token}
                        onChange={(e) => setFormData({ ...formData, auth_token: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        placeholder="Token used only when executing tests"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Description (optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateEndpoint}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                >
                  Create Endpoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingEndpoint && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Edit Endpoint</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Name *</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Environment</label>
                  <select
                    value={editFormData.environment}
                    onChange={(e) => setEditFormData({ ...editFormData, environment: e.target.value as typeof editFormData.environment })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="">Select environment</option>
                    {ENVIRONMENT_OPTIONS.map((env) => (
                      <option key={env} value={env}>{env}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">URL</label>
                  <input
                    type="text"
                    value={editFormData.base_url}
                    onChange={(e) => setEditFormData({ ...editFormData, base_url: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Auth Type</label>
                  <select
                    value={editFormData.auth_type}
                    onChange={(e) => setEditFormData({ ...editFormData, auth_type: e.target.value as AUTEndpointAuthType })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    {AUTH_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                {editFormData.auth_type !== 'none' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-200 mb-1">Auth Header</label>
                      <input
                        type="text"
                        value={editFormData.auth_header_name}
                        onChange={(e) => setEditFormData({ ...editFormData, auth_header_name: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        placeholder={editFormData.auth_type === 'bearer' ? 'Authorization' : 'X-API-Key'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-200 mb-1">
                        Auth Token {editingEndpoint.auth_configured ? '(leave blank to keep existing)' : '*'}
                      </label>
                      <input
                        type="password"
                        value={editFormData.auth_token}
                        onChange={(e) => setEditFormData({ ...editFormData, auth_token: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        placeholder={editingEndpoint.auth_configured ? 'Existing token is configured' : 'Token used only when executing tests'}
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">Description (optional)</label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => { setShowEditModal(false); setEditingEndpoint(null); }}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      if (!editFormData.name.trim()) {
                        addNotification('Endpoint name is required', 'error');
                        return;
                      }
                      const orgIdStr = localStorage.getItem('selectedOrganizationId');
                      const projectIdStr = localStorage.getItem('selectedProjectId');
                      const orgId = orgIdStr ? parseInt(orgIdStr) : undefined;
                      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
                      if (!orgId || !projectId || !editingEndpoint) return;
                      if (
                        editFormData.auth_type !== 'none' &&
                        !editingEndpoint.auth_configured &&
                        !editFormData.auth_token.trim()
                      ) {
                        addNotification('Auth token is required when endpoint auth is enabled', 'error');
                        return;
                      }
                      const endpointPayload = {
                        name: editFormData.name.trim(),
                        environment: editFormData.environment || undefined,
                        base_url: editFormData.base_url.trim() || undefined,
                        auth_type: editFormData.auth_type,
                        auth_header_name: editFormData.auth_header_name.trim(),
                        description: editFormData.description.trim() || undefined,
                        ...(editFormData.auth_token.trim()
                          ? { auth_token: editFormData.auth_token.trim() }
                          : {})
                      };
                      await autEndpointsApi.update(orgId, projectId, editingEndpoint.id, endpointPayload);
                      addNotification('Endpoint updated successfully', 'success');
                      setShowEditModal(false);
                      setEditingEndpoint(null);
                      loadEndpoints();
                    } catch (error) {
                      addNotification('Failed to update endpoint', 'error');
                      console.error(error);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Endpoints;
